import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const formData = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) formData.append(input.name, input.value);
    });

    try {
      await fetch('https://readdy.ai/api/form/d76ta13q7u9ee9hcel90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      alert('送信に失敗しました。お電話にてお問い合わせください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-primary">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-white/50 font-medium uppercase mb-3">Contact</span>
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-4">お問い合わせ</h2>
          <div className="w-16 h-0.5 bg-white/30 mx-auto mb-4"></div>
          <p className="text-white/60 text-sm">査定依頼・ご相談はお気軽にどうぞ。査定・出張費用は無料です。</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-white/50 mb-1">お電話でのお問い合わせ</p>
                  <a href="tel:092-577-9739" className="text-white text-lg font-semibold tracking-wide hover:text-white/80 cursor-pointer">
                    092-577-9739
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-white/50 mb-1">メールでのお問い合わせ</p>
                  <a href="mailto:info@botan-buy.com" className="text-white text-sm font-medium hover:text-white/80 cursor-pointer">
                    info@botan-buy.com
                  </a>
                </div>
              </div>
            </div>

            <a
              href="https://lin.ee/OGQq9z6"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#06C755] rounded-2xl p-6 flex items-center gap-4 hover:bg-[#05b34c] transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                <i className="ri-line-fill text-white text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-white/70 mb-1">LINEでのお問い合わせ</p>
                <p className="text-white text-sm font-semibold">天神屋 公式LINE</p>
                <p className="text-white/60 text-xs mt-0.5">タップして友だち追加</p>
              </div>
              <div className="ml-auto w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white/70"></i>
              </div>
            </a>

            <div className="bg-white/10 rounded-2xl p-6">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full mb-4">
                <i className="ri-map-pin-line text-white"></i>
              </div>
              <p className="text-xs text-white/50 mb-1">本社所在地</p>
              <p className="text-white text-sm leading-relaxed">
                〒810-0075<br />
                福岡県福岡市中央区港1-2-18-1712
              </p>
            </div>

            <div className="bg-white/10 rounded-2xl p-6">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full mb-4">
                <i className="ri-time-line text-white"></i>
              </div>
              <p className="text-xs text-white/50 mb-2">対応時間</p>
              <p className="text-white text-sm leading-relaxed">
                平日 / 土日祝<br />
                10:00 〜 19:00
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl p-10 flex flex-col items-center justify-center text-center h-full min-h-64">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-pale rounded-full mb-6">
                  <i className="ri-check-line text-2xl text-primary"></i>
                </div>
                <h3 className="text-xl font-semibold text-primary mb-3">送信完了しました</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  お問い合わせいただきありがとうございます。<br />
                  担当者より折り返しご連絡いたします。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form"
                className="bg-white rounded-2xl p-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      お名前 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="090-0000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">
                    メールアドレス <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@email.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">
                    お問い合わせ種別 <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="category"
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors bg-white cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="出張買取のご依頼（天神屋）">出張買取のご依頼（天神屋）</option>
                    <option value="カード買取・販売（まんがく）">カード買取・販売（まんがく）</option>
                    <option value="査定のご相談">査定のご相談</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">
                    お問い合わせ内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="お品物の詳細やご希望などをご記入ください..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors resize-none"
                  ></textarea>
                  <p className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                    {charCount}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full bg-primary text-white py-3.5 rounded-lg text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '送信中...' : '送信する'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  送信いただいた個人情報は、お問い合わせへの回答のみに使用します。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
